import { useTu } from "@src/appV2/i18n/useTu";
import { Autocomplete, CircularProgress, Stack, TextField } from "@mui/material";
import { Text } from "../../components/Text";

interface ResourceOption {
  id: string;
  displayName: string;
}

interface ResourceSearchAutocompleteProps<TOption extends ResourceOption> {
  error?: boolean;
  helperText?: string;
  isFetching: boolean;
  label: string;
  loadingText: string;
  noOptionsText: string;
  options: TOption[];
  placeholder?: string;
  value: TOption | null;
  /**
   * Optional controlled input text. Provide it (with `onInputChange`) when the
   * field can open with a pre-selected value, so MUI doesn't reset the box back
   * to the value's label and swallow typed characters. Omit for search-from-empty
   * flows, where MUI manages the input text itself.
   */
  inputValue?: string;
  onChange: (option: TOption | null) => void;
  onInputChange: (value: string) => void;
}

export function ResourceSearchAutocomplete<TOption extends ResourceOption>({
  error,
  helperText,
  isFetching,
  label,
  loadingText,
  noOptionsText,
  options,
  placeholder,
  value,
  inputValue,
  onChange,
  onInputChange,
}: ResourceSearchAutocompleteProps<TOption>) {
  const { tu } = useTu("requirementV2");

  return (
    <Autocomplete
      filterOptions={(filteredOptions) => filteredOptions}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.displayName)}
      handleHomeEndKeys
      isOptionEqualToValue={(option, selectedValue) => option.id === selectedValue.id}
      // Highlight the current text on focus so typing replaces the selected
      // value's label (starts a fresh search) instead of appending to it.
      selectOnFocus
      loading={isFetching}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      options={options}
      renderInput={(params) => (
        <TextField
          {...params}
          error={error}
          helperText={helperText}
          label={label}
          placeholder={placeholder}
          size="small"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching && <CircularProgress color="inherit" size={20} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(renderProps, option) => (
        <li {...renderProps} key={option.id}>
          <Stack direction="column">
            <Text variant="body2">{option.displayName}</Text>
            <Text sx={(theme) => ({ color: theme.palette.text.secondary })} variant="caption">
              {tu("ID: {{id}}", { values: { id: option.id } })}
            </Text>
          </Stack>
        </li>
      )}
      value={value}
      // When `inputValue` is undefined MUI manages the input text itself
      // (uncontrolled); when provided the consumer owns it (controlled).
      inputValue={inputValue}
      onChange={(_, newValue) => {
        onChange(typeof newValue === "string" ? null : newValue);
      }}
      onInputChange={(_, newInputValue, reason) => {
        if (reason === "input") {
          onInputChange(newInputValue);
        }
      }}
    />
  );
}
